import React from 'react';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import { CLERK_CONFIG } from './utils/clerk';
import Layout from './components/Layout';
import RoleGuard from './components/RoleGuard';
import Home from './pages/Home';
import SponsorDashboard from './pages/SponsorDashboard';
import CreateChallenge from './pages/CreateChallenge';
import CreateEvent from './pages/CreateEvent';
import HackdayDetails from './pages/HackdayDetails';
import CurrentEventsBounties from './components/CurrentEventsBounties';
import TeamRegistrationPage from './pages/TeamRegistrationPage';
import Earnings from './pages/Earnings';
import './App.css';
import CustomLoader from './components/CustomLoader';

const AppContent: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user, isLoading, userRole } = useAppContext();

  if (!isLoaded) {
    return <CustomLoader />;
  }

  if (isSignedIn) {
    // Show loading while checking user profile
    if (isLoading) {
      return (
        <CustomLoader />
      );
    }

    return (
      <div className="App min-h-screen font-space relative">
        <Layout>
          <Routes>
            {/* Redirect authenticated users from home to events */}
            <Route path="/" element={<Navigate to="/events" replace />} />

            {/* Sponsor-only routes */}
            <Route
              path="/sponsor/dashboard"
              element={<SponsorDashboard />}
            />
            <Route
              path="/sponsor/challenges/create"
              element={<CreateChallenge />}
            />
            <Route
              path="/sponsor/events/create"
              element={<CreateEvent />}
            />

            {/* Shared routes accessible by both roles */}
            <Route
              path="/hackday/:id"
              element={<HackdayDetails />}
            />

            {/* Additional shared routes */}
            <Route
              path="/events"
              element={<CurrentEventsBounties />}
            />

            {/* Team Registration Routes */}
            <Route
              path="/team-registration/:eventId"
              element={<TeamRegistrationPage />}
            />

            {/* Earnings & Payouts Route */}
            <Route
              path="/earnings"
              element={<Earnings />}
            />

            {/* My Events page removed — functionality merged into /events */}

            {/* Catch-all route - default to events */}
            <Route path="*" element={<Navigate to="/events" replace />} />
          </Routes>
        </Layout>
      </div>
    );
  }

  // Allow unauthenticated users to view the home page and public events
  return (
    <div className="App min-h-screen bg-background font-space">
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Public events page - redirect here instead of sign-in */}
          <Route path="/events" element={<Home />} />
          {/* Redirect all other routes to /events instead of sign-in */}
          <Route path="*" element={<Navigate to="/events" replace />} />
        </Routes>
      </Layout>
    </div>
  );
};

function App() {
  // Check if Clerk is configured
  if (!CLERK_CONFIG.publishableKey) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <h1 className="text-xl font-space font-semibold text-primary-text mb-4">
            Configuration Required
          </h1>
          <p className="text-primary-text font-space mb-4">
            Please set up your Clerk authentication keys to continue.
          </p>
          <p className="text-sm text-gray-600 font-space">
            Add your REACT_APP_CLERK_PUBLISHABLE_KEY to your environment variables.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider publishableKey={CLERK_CONFIG.publishableKey} appearance={CLERK_CONFIG.appearance}>
      <Router>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </Router>
    </ClerkProvider>
  );
}

export default App;
